import {
    ICorporationResponse,
    ILocationResponse,
    ISessionResponse,
    ISystemResponse,
} from '@kcftech/prometheus-client';
import { useCorporation } from 'custom_hooks/data_hooks/corporation/useCorporation';
import { useLocation } from 'custom_hooks/data_hooks/location/useLocation';
import { useSession } from 'custom_hooks/data_hooks/session/useSession';
import { useSystem } from 'custom_hooks/data_hooks/system/useSystem';
import { GlobalErrorPage } from 'pages/ErrorPage';
import { NotFound } from 'pages/NotFound';
import { createContext, ReactNode, useContext, useMemo } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { useQueryParams } from 'utils/useQueryParams';

/**
 * URL parameter name for a corporation.
 */
export const corporationRouteParam = 'corporationId';

/**
 * URL parameter name for a location.
 */
export const locationRouteParam = 'locationId';

/**
 * URL parameter name for a system.
 */
export const systemRouteParam = 'systemId';

export type RouteEntityParams = Readonly<{
    /**
     * Corporation id from the URL.
     */
    [corporationRouteParam]: string;
    /**
     * Location id from the URL.
     */
    [locationRouteParam]: string;
    /**
     * System id from the URL.
     */
    [systemRouteParam]: string;
}>;

/**
 * Base URL for a corporation route (`'/corporation/:corporationId'`).
 */
export const corporationRoute = `/corporation/:${corporationRouteParam}`;

/**
 * Base URL for a location route (`'/location/:locationId'`).
 */
export const locationRoute = `/location/:${locationRouteParam}`;

/**
 * Base URL for a system route (`'/system/:systemId'`).
 */
export const systemRoute = `/system/:${systemRouteParam}`;

export type RouteEntityType = 'corporation' | 'location' | 'system' | 'none';

export type RouteEntities = Readonly<{
    /**
     * The corporation loaded from the URL.
     */
    corporation?: ICorporationResponse;
    /**
     * The location loaded from the URL.
     */
    location?: ILocationResponse;
    /**
     * The system loaded from the URL.
     */
    system?: ISystemResponse;
    /**
     * The session loaded from the URL
     */
    session?: ISessionResponse;
    /**
     * Indicates if entities are currently being loaded.
     */
    isLoading: boolean;
    /**
     * The type of entity specified in the URL.
     */
    entityType: RouteEntityType;
}>;

export const RouteEntityContext = createContext<RouteEntities | undefined>(undefined);

/**
 * Returns the current route entities context.
 */
export function useRouteEntities(): RouteEntities {
    const ctx = useContext(RouteEntityContext);
    if (!ctx) {
        throw new Error(
            'Route entities context not available.  Did you forget to use <RouteEntityProvider>?',
        );
    }
    return ctx;
}

export type RouteCorporation = Readonly<{
    corporation: ICorporationResponse;
}>;

/**
 * Returns the current corporation from the route entities context.
 * Throws an error if a corporation is not available.
 */
export function useRouteCorporation(): RouteCorporation {
    const { corporation } = useRouteEntities();

    if (!corporation) {
        throw new Error('Missing corporation route entity.');
    }

    return { corporation };
}

export type RouteLocation = RouteCorporation &
    Readonly<{
        location: ILocationResponse;
    }>;

/**
 * Returns the current location from the route entities context.
 * Throws an error if a location is not available.
 */
export function useRouteLocation(): RouteLocation {
    const { corporation } = useRouteCorporation();
    const { location } = useRouteEntities();

    if (!location) {
        throw new Error('Missing location route entity.');
    }

    return { corporation, location };
}

export type RouteSystem = RouteLocation &
    Readonly<{
        system: ISystemResponse;
        session?: ISessionResponse;
    }>;

/**
 * Returns the current system from the route entities context.
 * Throws an error if a system is not available.
 */
export function useRouteSystem(): RouteSystem {
    const { corporation, location } = useRouteLocation();
    const { system, session } = useRouteEntities();

    if (!system) {
        throw new Error('Missing system route entity.');
    }

    return { corporation, location, system, session };
}

export type GetEntityTypeOptions = Readonly<{
    routeCorporationId?: string;
    routeLocationId?: string;
    routeSystemId?: string;
}>;

export const getRouteEntityType = ({
    routeCorporationId,
    routeLocationId,
    routeSystemId,
}: GetEntityTypeOptions): RouteEntityType => {
    if (routeSystemId) {
        return 'system';
    }
    if (routeLocationId) {
        return 'location';
    }
    if (routeCorporationId) {
        return 'corporation';
    }
    return 'none';
};

export type EntityProviderProps = Readonly<{
    corporationId?: string;
    locationId?: string;
    systemId?: string;
    sessionId?: string;
    entityType?: RouteEntityType;
    children: ReactNode;
}>;

/**
 * Fetches and provides corporation, location, and system entities by ID.
 *
 * This is used to create a context that provides the contextual entities
 * without them appearing in the URL (useful in global modules that render
 * components that expect contextual entities).
 *
 * IDs cascade: a systemId will resolve its location and corporation
 * automatically when explicit IDs are not provided. (but this should
 * be avoided when possible for performance)
 */
export function EntityProvider({
    corporationId: explicitCorpId,
    locationId: explicitLocId,
    systemId,
    sessionId,
    entityType: explicitEntityType,
    children,
}: EntityProviderProps) {
    const { data: maybeSystem, isLoading: isLoadingSystem } = useSystem(systemId);
    // a deleted or missing system yields 'null'. Consumers handle `undefined`
    // just fine.
    const system = maybeSystem ?? undefined;

    const {
        data: location,
        isLoading: isLoadingLocation,
        isFetching: isFetchingLocation,
    } = useLocation(explicitLocId ?? system?.Location.LocationId);

    const { data: corporation, isLoading: isLoadingCorporation } = useCorporation(
        explicitCorpId ??
            system?.Corporation.CorporationId ??
            location?.Corporation.CorporationId,
    );

    const { data: session } = useSession(sessionId ?? '');

    const entityType =
        explicitEntityType ??
        getRouteEntityType({
            routeCorporationId: explicitCorpId,
            routeLocationId: explicitLocId,
            routeSystemId: systemId,
        });

    const isLoading =
        isLoadingCorporation ||
        isLoadingLocation ||
        isFetchingLocation ||
        isLoadingSystem;

    const ctx = useMemo(
        () => ({
            corporation,
            location,
            system,
            isLoading,
            session,
            entityType,
        }),
        [corporation, entityType, isLoading, location, session, system],
    );

    if (isLoading) {
        return null;
    }

    return (
        <RouteEntityContext.Provider value={ctx}>{children}</RouteEntityContext.Provider>
    );
}

export type RouteEntityProviderProps = Readonly<{
    children: ReactNode;
}>;

/**
 * Creates a context that is populated with the current corporation,
 * location, and system from the URL.
 */
export function RouteEntityProvider({ children }: RouteEntityProviderProps) {
    const routeMatch = useRouteMatch<RouteEntityParams>([
        systemRoute,
        locationRoute,
        corporationRoute,
    ]);

    const routeSystemId = routeMatch?.params[systemRouteParam];
    const routeLocationId = routeMatch?.params[locationRouteParam];
    const routeCorporationId = routeMatch?.params[corporationRouteParam];

    const {
        data: maybeSystem,
        isLoading: isLoadingSystem,
        error: systemError,
    } = useSystem(routeSystemId);
    const isSystemMissing = maybeSystem === null;
    const system = maybeSystem ?? undefined;

    const {
        data: location,
        isLoading: isLoadingLocation,
        error: locationError,
        isFetching: isFetchingLocation,
    } = useLocation(routeLocationId ?? system?.Location.LocationId);

    const {
        data: corporation,
        isLoading: isLoadingCorporation,
        error: corporationError,
    } = useCorporation(
        routeCorporationId ??
            system?.Corporation.CorporationId ??
            location?.Corporation.CorporationId,
    );

    const { data: session } = useSession(useQueryParams().get('sessionId') ?? '');

    const entityType = getRouteEntityType({
        routeCorporationId,
        routeLocationId,
        routeSystemId,
    });

    const isLoading =
        isLoadingCorporation ||
        isLoadingLocation ||
        isFetchingLocation ||
        isLoadingSystem;
    const error = corporationError ?? locationError ?? systemError ?? undefined;

    const ctx = useMemo(
        () => ({
            corporation,
            location,
            system,
            isLoading,
            session,
            entityType,
        }),
        [corporation, entityType, isLoading, location, session, system],
    );

    if (isSystemMissing) {
        return <NotFound />;
    }

    if (error) {
        const isNotFoundError =
            'message' in error &&
            typeof error.message === 'string' &&
            error.message.match(/not found/i);

        return isNotFoundError ? <NotFound /> : <GlobalErrorPage />;
    }

    return (
        <RouteEntityContext.Provider value={ctx}>{children}</RouteEntityContext.Provider>
    );
}

export type MockRouteEntityProviderProps = Pick<
    RouteEntities,
    'corporation' | 'location' | 'system' | 'session' | 'entityType'
> &
    Readonly<{
        children?: ReactNode;
    }>;

export const MockRouteEntityProvider = ({
    corporation,
    location,
    system,
    session,
    entityType,
    children,
}: MockRouteEntityProviderProps) => {
    const value = useMemo<RouteEntities>(
        () => ({
            isLoading: false,
            corporation,
            location,
            system,
            session,
            entityType,
        }),
        [corporation, entityType, location, session, system],
    );

    return (
        <RouteEntityContext.Provider value={value}>
            {children}
        </RouteEntityContext.Provider>
    );
};
